import {
  Box,
  Icon,
  Input,
  InputGroup,
  InputProps,
  InputRightElement,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { IconType } from "react-icons";
import { RiTimeFill } from "react-icons/ri";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);
const ROW_HEIGHT = 36;

function pad(n: number) {
  return String(n).padStart(2, "0");
}

function parseTime(value?: string): { hour: number; minute: number } | null {
  if (!value) return null;
  const [h, m] = value.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return { hour: h, minute: m };
}

/** Validates a typed "H:mm"/"HH:mm" 24-hour value and normalizes it to zero-padded "HH:mm". */
function parseTypedTime(text: string): string | null {
  const match = text.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return `${pad(hour)}:${pad(minute)}`;
}

export interface TimePickerProps {
  /** "HH:mm", 24-hour, matching what a native `<input type="time">` would produce. */
  value?: string;
  onChange: (value: string) => void;
  size?: InputProps["size"];
  disabled?: boolean;
  placeholder?: string;
  /** Inclusive "HH:mm" bounds. */
  minTime?: string;
  maxTime?: string;
  icon?: IconType;
  /** When true, the input can also be typed into directly ("HH:mm"), not just picked from the popover. */
  allowTyping?: boolean;
}

function TimeColumn({
  values,
  selected,
  isDisabled,
  onSelect,
  hoverBg,
  textColor,
}: {
  values: number[];
  selected: number | null;
  isDisabled: (value: number) => boolean;
  onSelect: (value: number) => void;
  hoverBg: string;
  textColor: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selected == null || !containerRef.current) return;
    const row = containerRef.current.querySelector<HTMLElement>(`[data-value="${selected}"]`);
    row?.scrollIntoView({ block: "center" });
  }, [selected]);

  return (
    <Box
      ref={containerRef}
      overflowY="auto"
      h={`${ROW_HEIGHT * 5}px`}
      w="60px"
      py={`${ROW_HEIGHT * 2}px`}
      css={{
        scrollSnapType: "y mandatory",
        scrollbarWidth: "thin",
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          background: "var(--chakra-colors-surface-200, #cbd5e0)",
          borderRadius: "999px",
        },
      }}
    >
      {values.map((v) => {
        const disabled = isDisabled(v);
        const isSelected = v === selected;
        return (
          <Box
            key={v}
            data-value={v}
            as="button"
            type="button"
            display="flex"
            alignItems="center"
            justifyContent="center"
            mx="auto"
            w="44px"
            h={`${ROW_HEIGHT - 4}px`}
            my="2px"
            css={{ scrollSnapAlign: "center" }}
            fontSize="sm"
            borderRadius="10px"
            fontWeight={isSelected ? "700" : "400"}
            bg={isSelected ? "primary.500" : "transparent"}
            color={isSelected ? "white" : disabled ? "surface.200" : textColor}
            cursor={disabled ? "not-allowed" : "pointer"}
            transition="background-color 0.15s ease, color 0.15s ease"
            _hover={disabled ? {} : { bg: isSelected ? "primary.500" : hoverBg }}
            disabled={disabled}
            onClick={() => !disabled && onSelect(v)}
          >
            {pad(v)}
          </Box>
        );
      })}
    </Box>
  );
}

export function TimePicker({
  value,
  onChange,
  size = "md",
  disabled,
  placeholder,
  minTime,
  maxTime,
  icon = RiTimeFill,
  allowTyping = false,
}: TimePickerProps) {
  const { isOpen, onOpen, onClose, onToggle } = useDisclosure();
  const [typedValue, setTypedValue] = useState<string | null>(null);

  useEffect(() => {
    setTypedValue(null);
  }, [value]);

  function commitTypedValue(text: string) {
    if (!text.trim()) {
      setTypedValue(null);
      return;
    }
    const parsedValue = parseTypedTime(text);
    if (parsedValue) onChange(parsedValue);
    setTypedValue(null);
  }

  const surfaceBg = useColorModeValue("white", "navy.800");
  const shadow = useColorModeValue(
    "14px 17px 40px 4px rgba(112, 144, 176, 0.18)",
    "14px 17px 40px 4px rgba(112, 144, 176, 0.06)",
  );
  const hoverBg = useColorModeValue("surface.100", "whiteAlpha.100");
  const textColor = useColorModeValue("gray.800", "white");

  const parsed = useMemo(() => parseTime(value), [value]);
  const min = useMemo(() => parseTime(minTime), [minTime]);
  const max = useMemo(() => parseTime(maxTime), [maxTime]);

  function isHourDisabled(hour: number) {
    if (min && hour < min.hour) return true;
    if (max && hour > max.hour) return true;
    return false;
  }
  function isMinuteDisabled(minute: number) {
    const hour = parsed?.hour ?? 0;
    if (min && hour === min.hour && minute < min.minute) return true;
    if (max && hour === max.hour && minute > max.minute) return true;
    return false;
  }

  function selectHour(hour: number) {
    onChange(`${pad(hour)}:${pad(parsed?.minute ?? 0)}`);
  }
  function selectMinute(minute: number) {
    onChange(`${pad(parsed?.hour ?? 0)}:${pad(minute)}`);
  }

  return (
    <Popover
      isOpen={disabled ? false : isOpen}
      onOpen={onOpen}
      onClose={onClose}
      placement="bottom-start"
      isLazy
    >
      <PopoverTrigger>
        <InputGroup
          position="relative"
          display="flex"
          alignItems="center"
          justifyContent="center"
          size={size}
        >
          <Input
            size={size}
            value={typedValue ?? value ?? ""}
            placeholder={placeholder}
            isReadOnly={!allowTyping}
            isDisabled={disabled}
            autoComplete="off"
            cursor={disabled ? "default" : allowTyping ? "text" : "pointer"}
            onClick={() => !disabled && !allowTyping && onToggle()}
            onChange={
              allowTyping ? (e) => setTypedValue(e.target.value) : undefined
            }
            onBlur={
              allowTyping ? (e) => commitTypedValue(e.target.value) : undefined
            }
            onKeyDown={
              allowTyping
                ? (e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      commitTypedValue(e.currentTarget.value);
                    }
                  }
                : undefined
            }
          />
          <InputRightElement
            color="surface.600"
            cursor={disabled ? "default" : "pointer"}
            onClick={() => !disabled && onToggle()}
          >
            <Icon as={icon} />
          </InputRightElement>
        </InputGroup>
      </PopoverTrigger>
      <Portal>
      <PopoverContent
        border="none"
        boxShadow={shadow}
        borderRadius="16px"
        bg={surfaceBg}
        w="auto"
        zIndex="popover"
      >
        <PopoverBody
          p={2}
          display="flex"
          gap={1}
          alignItems="center"
          position="relative"
        >
          <Box
            position="absolute"
            top="50%"
            left={2}
            right={2}
            h={`${ROW_HEIGHT}px`}
            transform="translateY(-50%)"
            bg={hoverBg}
            borderRadius="10px"
            pointerEvents="none"
            zIndex={0}
          />
          <Box zIndex={1}>
            <TimeColumn
              values={HOURS}
              selected={parsed?.hour ?? null}
              isDisabled={isHourDisabled}
              onSelect={selectHour}
              hoverBg={hoverBg}
              textColor={textColor}
            />
          </Box>
          <Box fontWeight="700" color="surface.300" zIndex={1}>
            :
          </Box>
          <Box zIndex={1}>
            <TimeColumn
              values={MINUTES}
              selected={parsed?.minute ?? null}
              isDisabled={isMinuteDisabled}
              onSelect={selectMinute}
              hoverBg={hoverBg}
              textColor={textColor}
            />
          </Box>
        </PopoverBody>
      </PopoverContent>
      </Portal>
    </Popover>
  );
}
